import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  ArrowLeft,
  BellPlus,
  MessageCircleQuestion,
  BookmarkCheck,
  BookmarkPlus,
  Building2,
  CalendarDays,
  CircleAlert,
  ClipboardList,
  FileStack,
  FileText,
  LoaderCircle,
  Hash,
  IndianRupee,
  MapPin,
  Phone,
  TriangleAlert,
  UserRound,
  Globe,
  Info,
  Pause,
  Play,
  RotateCw,
  Share2,
  Square,
  Volume2,
  Send,
} from 'lucide-react'
import { languageInfo } from '../i18n/languages'
import { pack, type LanguagePack } from '../i18n/packs'
import { builtInVoiceSupported, renderVoiceNote } from '../services/speech'
import { daysUntil, explain, isInLanguageScript, type CardId, type ExplainItem } from '../services/explain'
import { askConfigured } from '../services/ask'
import { buildIcs, openInCalendar, REMINDER_KINDS } from '../services/calendar'
import { maskAadhaar } from '../services/extract'
import { MAX_TRANSLATE_LINES, translatableLines, translateLines, translationConfigured } from '../services/translate'
import type { useSpeech } from '../hooks/useSpeech'
import type { DateFact, Language, OcrResult, StructuredDocument } from '../types/document'
import { AskPanel } from './AskPanel'
import { LabResults } from './LabResults'
import { ClauseList } from './ClauseList'
import { HelpPanel } from './HelpPanel'
import { LanguagePicker } from './LanguagePicker'

type Speech = ReturnType<typeof useSpeech>

const CARD_ICONS: Record<CardId, typeof CalendarDays> = {
  todo: ClipboardList,
  dates: CalendarDays,
  amount: IndianRupee,
  where: MapPin,
  warnings: TriangleAlert,
  docs: FileStack,
  person: UserRound,
  from: Building2,
  numbers: Hash,
  contact: Phone
}

export function Result({
  p,
  language,
  onLanguage,
  doc,
  ocr,
  speech,
  saved,
  onSave,
  onShare,
  onNew,
  onRetryRotated,
  onOpenLegal,
  helperMode = false,
  listenMode = false
}: {
  p: LanguagePack
  language: Language
  onLanguage: (language: Language) => void
  doc: StructuredDocument
  ocr: OcrResult | null
  speech: Speech
  saved: boolean
  onSave: () => void
  onShare: (text: string) => void
  /** Helper mode: no saving; a prominent "Next person" clears everything. */
  helperMode?: boolean
  /** Listen mode: read the explanation aloud as soon as it is shown. */
  listenMode?: boolean
  onNew: () => void
  onRetryRotated?: () => void
  onOpenLegal: () => void
}) {
  const explanation = useMemo(() => explain(doc, language, p), [doc, language, p])
  const { prepare, voicesLoaded } = speech
  // Warm up the built-in voice (if already on the phone) so Sunao starts instantly
  useEffect(() => {
    prepare(explanation.speech, language)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explanation, language, voicesLoaded])

  // Listen mode: speak the explanation as soon as it is shown (the person
  // already tapped to read the paper, so the browser allows audio)
  useEffect(() => {
    if (listenMode) speech.play(explanation.speech, language)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listenMode, explanation, language])

  const shareText = [
    explanation.heading,
    ...explanation.seedhiBaat.map((s) => s.text),
    ...explanation.cards.flatMap((c) => [`${c.icon} ${c.title}`, ...c.items.map((i) => i.text ?? i.evidence)]),
    '',
    p.disclaimer
  ]
    .filter((line) => line !== undefined)
    .join('\n')

  return (
    <section className="result-view">
      <div className="result-topbar">
        <button className="back-button" onClick={onNew}>
          <ArrowLeft size={18} aria-hidden="true" /> {p.newDocument}
        </button>
        <LanguagePicker id="result-language" compact label="" value={language} onChange={onLanguage} />
      </div>
      {helperMode && (
        <button className="primary-button next-person" onClick={onNew}>
          <UserRound size={20} aria-hidden="true" /> {p.nextPerson}
        </button>
      )}

      <header className="result-heading">
        <h1>{explanation.heading}</h1>
        {explanation.about && <p className="type-about">{explanation.about}</p>}
      </header>

      <SpeechControls p={p} language={language} doc={doc} sentences={explanation.speech} speech={speech} />
      <VoiceNoteButton p={p} language={language} sentences={explanation.speech} />

      {explanation.lowQuality && (
        <div className="notice-box">
          <CircleAlert size={20} aria-hidden="true" />
          <div>
            <span>{p.lowQuality}</span>
            {onRetryRotated && (
              <button className="inline-action" onClick={onRetryRotated}>
                <RotateCw size={16} aria-hidden="true" /> {p.retryRotated}
              </button>
            )}
          </div>
        </div>
      )}

      <section className="seedhi-baat">
        <h2>{p.seedhiBaat}</h2>
        <ul>
          {explanation.seedhiBaat.map((item, index) => (
            <li key={index}>
              <p>{item.text}</p>
              {item.unclear && <UnclearNote p={p} />}
            </li>
          ))}
        </ul>
      </section>

      {doc.labTests && doc.labTests.length > 0 && <LabResults tests={doc.labTests} language={language} />}
      {['rental_agreement', 'loan_agreement', 'contract'].includes(doc.documentType) || (doc.clauses && doc.clauses.length > 0) ? (
        <ClauseList clauses={doc.clauses ?? []} language={language} docSays={p.docSays} />
      ) : null}

      <div className="fact-cards">
        {explanation.cards.map((card) => {
          const Icon = CARD_ICONS[card.id]
          return (
          <section className={`fact-card card-${card.id}`} key={card.id}>
            <h2>
              <Icon size={20} strokeWidth={2} aria-hidden="true" /> {card.title}
            </h2>
            <ul>
              {card.items.map((item, index) => (
                <FactItem
                  key={index}
                  item={item}
                  p={p}
                  action={card.id === 'dates' ? <ReminderButton p={p} fact={doc.dates[index]} text={item.text ?? ''} /> : undefined}
                />
              ))}
            </ul>
          </section>
          )
        })}
      </div>

      {askConfigured() && (
        <AskPanel key={`ask-${language}`} p={p} language={language} doc={doc} ocr={ocr} speech={speech} onShare={onShare} />
      )}

      <HelpPanel p={p} doc={doc} canAsk={askConfigured()} onOpenLegal={onOpenLegal} />

      {translationConfigured() && (
        <TranslatePanel key={language} p={p} language={language} doc={doc} />
      )}

      {ocr && <FullText p={p} ocr={ocr} />}

      <div className="disclaimer-box">
        <Info size={20} aria-hidden="true" />
        <p>{p.disclaimer}</p>
      </div>

      <div className="result-actions">
        {!helperMode && (
          <button className="secondary-button" onClick={onSave} disabled={saved}>
            {saved ? <BookmarkCheck size={20} aria-hidden="true" /> : <BookmarkPlus size={20} aria-hidden="true" />}
            {saved ? p.saved : p.save}
          </button>
        )}
        <button className="secondary-button" onClick={() => onShare(shareText)}>
          <Share2 size={20} aria-hidden="true" /> {p.share}
        </button>
        <button className="primary-button" onClick={onNew}>
          <FileText size={20} aria-hidden="true" /> {helperMode ? p.nextPerson : p.newDocument}
        </button>
      </div>

      {askConfigured() && <AskJumpButton p={p} />}
    </section>
  )
}

/** Floating "Ask a question" button, hidden while the Ask panel is on screen. */
function AskJumpButton({ p }: { p: LanguagePack }) {
  const [hidden, setHidden] = useState(false)
  useEffect(() => {
    const panel = document.getElementById('ask')
    if (!panel || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => setHidden(entry.isIntersecting || entry.boundingClientRect.top < 0), {
      rootMargin: '0px 0px -20% 0px'
    })
    observer.observe(panel)
    return () => observer.disconnect()
  }, [])
  if (hidden) return null
  return (
    <button
      className="ask-fab"
      onClick={() => {
        document.getElementById('ask')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setTimeout(() => document.querySelector<HTMLTextAreaElement>('#ask .ask-input')?.focus({ preventScroll: true }), 450)
      }}
    >
      <MessageCircleQuestion size={22} aria-hidden="true" /> {p.askJump}
    </button>
  )
}

/** "Add reminder to calendar" for an upcoming date the person has to act on. */
function ReminderButton({ p, fact, text }: { p: LanguagePack; fact: DateFact | undefined; text: string }) {
  if (!fact?.iso || !REMINDER_KINDS.includes(fact.kind) || daysUntil(fact.iso) < 0) return null
  const iso = fact.iso
  return (
    <button
      className="inline-action reminder-button"
      onClick={() =>
        void openInCalendar(
          buildIcs({ iso, kind: fact.kind, title: `Samjho: ${text || fact.value}`, description: `${p.docSays}: “${fact.evidence}”\n\n${p.disclaimer}` }),
          iso
        )
      }
    >
      <BellPlus size={16} aria-hidden="true" /> {p.addToCalendar}
    </button>
  )
}

function UnclearNote({ p }: { p: LanguagePack }) {
  return (
    <p className="unclear-note">
      <CircleAlert size={15} aria-hidden="true" /> {p.unclearText}
    </p>
  )
}

function FactItem({ item, p, action }: { item: ExplainItem; p: LanguagePack; action?: ReactNode }) {
  return (
    <li className="fact-item">
      {item.text && <p className="samjho-text">{item.text}</p>}
      {item.evidence && (
        <blockquote className="evidence">
          <span className="evidence-label">{p.docSays}</span>
          <span className="evidence-text">“{item.evidence}”</span>
        </blockquote>
      )}
      {item.unclear && <UnclearNote p={p} />}
      {action}
    </li>
  )
}

function SpeechControls({
  p,
  language,
  doc,
  sentences,
  speech
}: {
  p: LanguagePack
  language: Language
  doc: StructuredDocument
  sentences: string[]
  speech: Speech
}) {
  if (!speech.supported) {
    return <p className="speech-note">{p.noSpeechSupport}</p>
  }

  if (speech.state !== 'idle') {
    return (
      <div className="speech-bar">
        {speech.state === 'loading' ? (
          <button className="listen-all-button active-speaking" disabled aria-busy="true">
            <LoaderCircle className="spin" size={24} aria-hidden="true" /> {p.listen}…
          </button>
        ) : speech.state === 'speaking' ? (
          <button className="listen-all-button active-speaking" onClick={speech.pause}>
            <Pause size={24} aria-hidden="true" /> {p.pause}
          </button>
        ) : (
          <button className="listen-all-button" onClick={speech.resume}>
            <Play size={24} aria-hidden="true" /> {p.resume}
          </button>
        )}
        <button className="stop-button" onClick={speech.stop} aria-label={p.stop}>
          <Square size={20} aria-hidden="true" /> {p.stop}
        </button>
        {speech.state === 'loading' && speech.usesBuiltInVoice(language) && (
          <p className="speech-note speech-bar-note">{p.voiceDownloadNote}</p>
        )}
      </div>
    )
  }

  if (!speech.hasVoice(language)) {
    // No voice for this language on the device: explain, and offer Hindi/English instead
    const fallbacks = (['hi', 'en'] as Language[]).filter((l) => l !== language && speech.hasVoice(l))
    return (
      <div className="notice-box">
        <Volume2 size={20} aria-hidden="true" />
        <div>
          <strong>{p.noVoice}</strong>
          <span className="block">{p.noVoiceHint}</span>
          <div className="fallback-row">
            {fallbacks.map((l) => (
              <button
                key={l}
                className="inline-action"
                onClick={() => speech.play(explain(doc, l, pack(l)).speech, l)}
              >
                <Volume2 size={16} aria-hidden="true" /> {l === 'hi' ? p.listenInHindi : p.listenInEnglish}
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <button className="listen-all-button" onClick={() => speech.play(sentences, language)}>
      <Volume2 size={26} aria-hidden="true" /> {p.listen}
    </button>
  )
}

function TranslatePanel({ p, language, doc }: { p: LanguagePack; language: Language; doc: StructuredDocument }) {
  // Lines not in the reader's language: English ones (IndicTrans2) and lines in
  // another Indian language (AI model), in a stable order.
  const items = useMemo(() => {
    const candidates = [
      ...doc.actions.map((a) => a.evidence),
      ...doc.warnings.map((w) => w.evidence),
      doc.place?.evidence,
      doc.status?.evidence
    ]
    const unique = [...new Set(candidates.filter((l): l is string => Boolean(l)))]
    return [...translatableLines(unique, language).entries()]
      .flatMap(([source, group]) => group.map((line) => ({ line, source })))
      .slice(0, MAX_TRANSLATE_LINES)
  }, [doc, language])
  const lines = items.map((item) => item.line)
  const needsAi = items.some((item) => item.source !== 'en')
  const [state, setState] = useState<'idle' | 'consent' | 'loading' | 'done' | 'error'>('idle')
  const [results, setResults] = useState<string[]>([])

  if (items.length === 0) return null

  async function run() {
    setState('loading')
    try {
      const out: string[] = []
      for (const source of [...new Set(items.map((item) => item.source))]) {
        const indexes = items.flatMap((item, i) => (item.source === source ? [i] : []))
        const translated = await translateLines(indexes.map((i) => items[i].line), language, source)
        indexes.forEach((i, k) => (out[i] = translated[k]))
      }
      setResults(out)
      setState('done')
    } catch {
      setState('error')
    }
  }

  return (
    <section className="translate-panel">
      {state === 'idle' && (
        <button className="secondary-button" onClick={() => setState('consent')}>
          <Globe size={20} aria-hidden="true" /> {needsAi ? p.translateOtherLines : p.translateLines}
        </button>
      )}
      {state === 'consent' && (
        <div className="consent-box">
          <p>{needsAi ? `${p.translateConsent} ${p.translateAiNote}` : p.translateConsent}</p>
          <div className="fallback-row">
            <button className="primary-button" onClick={run}>{p.translateConfirm}</button>
            <button className="text-button" onClick={() => setState('idle')}>{p.cancel}</button>
          </div>
        </div>
      )}
      {state === 'loading' && <p className="speech-note">{p.translating}</p>}
      {state === 'error' && <p className="speech-note">{p.translateFailed}</p>}
      {state === 'done' && (
        <>
          <p className="translate-note">
            <Globe size={16} aria-hidden="true" /> {p.translateNote}
          </p>
          <ul className="translation-list">
            {lines.map((line, index) => (
              <li key={index}>
                <p className="samjho-text">{results[index]}</p>
                <blockquote className="evidence">
                  <span className="evidence-label">{p.docSays}</span>
                  <span className="evidence-text">“{line}”</span>
                </blockquote>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

function FullText({ p, ocr }: { p: LanguagePack; ocr: OcrResult }) {
  const unclearCount = ocr.lines.filter((l) => l.text.trim() && l.confidence < 60).length
  return (
    <details className="full-text">
      <summary>{p.fullText}</summary>
      {unclearCount > 0 && <p className="field-hint">{p.fullTextHint} {p.errUnclearPart}</p>}
      <div className="ocr-lines">
        {ocr.lines.map((line, index) =>
          line.text.trim() ? (
            <p key={index} className={line.confidence < 60 ? 'ocr-unclear' : undefined}>
              {maskAadhaar(line.text)}
            </p>
          ) : (
            <br key={index} />
          )
        )}
      </div>
    </details>
  )
}

/** Sends the explanation as a voice note (made on the phone with the built-in voice). */
function VoiceNoteButton({ p, language, sentences }: { p: LanguagePack; language: Language; sentences: string[] }) {
  const [state, setState] = useState<'idle' | 'making' | 'failed'>('idle')
  if (!languageInfo(language).voice || !builtInVoiceSupported() || typeof navigator.canShare !== 'function') return null

  async function send() {
    setState('making')
    try {
      const file = await renderVoiceNote(sentences, language)
      if (!navigator.canShare({ files: [file] })) throw new Error('cannot-share-files')
      await navigator.share({ files: [file] })
      setState('idle')
    } catch (error) {
      setState(error instanceof DOMException && error.name === 'AbortError' ? 'idle' : 'failed')
    }
  }

  return (
    <div className="voice-note">
      <button className="secondary-button" onClick={send} disabled={state === 'making'} aria-busy={state === 'making'}>
        <Send size={20} aria-hidden="true" /> {state === 'making' ? p.makingVoiceNote : p.shareVoiceNote}
      </button>
      {state === 'failed' && <p className="speech-note">{p.voiceNoteFailed}</p>}
    </div>
  )
}
